/**
 * 客户授权管控的开发环境模拟开关
 *
 * 单独拆一个文件是因为这里用到了 import.meta.env（只有 Vite 能处理），
 * 放在 customerAuthGate.ts 里会让那个模块无法被 jest 引入。
 */

export type DevAuthGateOverride = {
  /** 模拟「超管后台环境」；null 表示不覆盖，用真实的 window.isFromAdmin */
  fromAdmin: boolean | null
  /** 模拟「客户是否授权」；null 表示不覆盖，按 URL 上的 access_scope 判定 */
  isAuth: boolean | null
}

export const EMPTY_DEV_AUTH_GATE: DevAuthGateOverride = {
  fromAdmin: null,
  isAuth: null,
}

const STORAGE_KEY = "pp:dev:customer-auth-gate"

/** 开发环境模拟开关只在 dev 构建里生效，生产包内所有 override 恒为 null */
export const isDevAuthGateEnabled = (): boolean => import.meta.env.DEV

const normalize = (value: unknown): boolean | null => (typeof value === "boolean" ? value : null)

export const writeDevAuthGate = (value: DevAuthGateOverride): void => {
  if (!isDevAuthGateEnabled()) return

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch (e) {
    console.debug("[customerAuthGate] 写入本地模拟开关失败", e)
  }
}

/**
 * 读取开发环境模拟开关
 *
 * 支持两种入口，URL 参数优先并会写回 localStorage：
 * - URL：`?_dev_from_admin=1&_dev_is_auth=0`
 * - localStorage：`pp:dev:customer-auth-gate`
 */
export const readDevAuthGate = (): DevAuthGateOverride => {
  if (!isDevAuthGateEnabled()) return EMPTY_DEV_AUTH_GATE

  let stored: DevAuthGateOverride = EMPTY_DEV_AUTH_GATE

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)

    if (raw) {
      const parsed = JSON.parse(raw)

      stored = {
        fromAdmin: normalize(parsed?.fromAdmin),
        isAuth: normalize(parsed?.isAuth),
      }
    }
  } catch (e) {
    console.debug("[customerAuthGate] 读取本地模拟开关失败", e)
  }

  const params = new URLSearchParams(window.location.search)
  const fromUrl = (key: string): boolean | null => {
    if (!params.has(key)) return null
    const value = (params.get(key) || "").trim()
      .toLowerCase()

    return !["0", "false", "no", "off", ""].includes(value)
  }

  const next: DevAuthGateOverride = {
    fromAdmin: fromUrl("_dev_from_admin") ?? stored.fromAdmin,
    isAuth: fromUrl("_dev_is_auth") ?? stored.isAuth,
  }

  if (next.fromAdmin !== stored.fromAdmin || next.isAuth !== stored.isAuth) {
    writeDevAuthGate(next)
  }

  return next
}
